import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '@/src/store'
import type { FeaturePhase, FeatureVersion } from '@/src/types/featureExperience'
import { selectResolvedFeature } from '@/src/store/apps/featureExperience'
import { daysUntilCutoverUtc, isCutoverInstantReached } from '@/src/utils/featureExperienceDates'
import { isUserInFeatureRolloutAudience } from '@/src/utils/featureExperienceAudience'
import { isResolvedForced } from '@/src/utils/featureExperienceResolve'

export interface UseFeatureVersionResult {
  version: FeatureVersion
  canToggle: boolean
  cutoverDate: string | null
  isLoading: boolean
  phase: FeaturePhase
  forceVersion: boolean

  /** True when `forceVersion`, `phase === 'forced'`, or `reason === 'forced'` — no classic opt-out. */
  isForcedResolution: boolean
  reason?: string
  lockAfterOptInToNext: boolean
  daysUntilCutover: number | null

  /** Client-side: current time is at or after `cutoverDate` (toggle should be hidden). */
  cutoverReached: boolean

  /** `false` when user should see no rollout chrome (toggle slot, banners) — see `isUserInFeatureRolloutAudience`. */
  isInRolloutAudience: boolean
  error: string | null
}

export function useFeatureVersion(featureKey: string): UseFeatureVersionResult {
  const feature = useSelector((state: RootState) => selectResolvedFeature(featureKey)(state))
  const status = useSelector((state: RootState) => state.featureExperience.status)
  const error = useSelector((state: RootState) => state.featureExperience.error)

  const daysUntilCutover = useMemo(() => daysUntilCutoverUtc(feature?.cutoverDate), [feature?.cutoverDate])

  const cutoverReached = useMemo(() => isCutoverInstantReached(feature?.cutoverDate), [feature?.cutoverDate])

  const isInRolloutAudience = useMemo(() => isUserInFeatureRolloutAudience(feature), [feature])

  const isForcedResolution = useMemo(() => isResolvedForced(feature), [feature])

  /**
   * Loading should be interpreted per-feature-key, not just per-slice status.
   * The slice `status` can be:
   * - `idle` before any resolve call
   * - `succeeded` after resolving *other* feature keys
   * In both cases, `feature` can still be undefined for this key and callers must treat it as "not yet resolved".
   */
  const isLoading = !feature && (status === 'idle' || status === 'loading' || status === 'succeeded')

  return {
    version: feature?.resolvedVersion ?? 'classic',
    canToggle: feature?.canToggle ?? false,
    cutoverDate: feature?.cutoverDate ?? null,
    isLoading,
    phase: feature?.phase ?? 'available',
    forceVersion: feature?.forceVersion ?? false,
    isForcedResolution,
    reason: feature?.reason,
    lockAfterOptInToNext: feature?.lockAfterOptInToNext ?? false,
    daysUntilCutover,
    cutoverReached,
    isInRolloutAudience,
    error
  }
}
